/**
 * Where a signed-in account belongs when no particular page was asked for
 * (issue #384, item 3 — Andreas's decision of 2026-08-20).
 *
 * THE DECISION THIS ENCODES. The coach Dashboard (#74) is thin enough to be
 * noise until the coach side grows (#314, #383), so a coach no longer LANDS
 * on it: they land on My Players, the surface a 1:1 coach actually works
 * from. It is HIDDEN, not deleted — `pages/Dashboard.jsx`'s `CoachDashboard`
 * is untouched and the route still exists — so the reversal is one `roles`
 * array in App.js, not a rebuild.
 *
 * WHY A FUNCTION AND NOT A CONSTANT PER CALLER. "The dashboard" was written
 * out as a literal in eight places before this: the router's role bounce,
 * two logo links, the demo role switch, the landing page's signed-in
 * redirect, sign-in's fallback, the demo "Explore as…" hand-off, and
 * `/performance`'s own coach guard (that last one is gone as of #719 —
 * `/performance` states `roles` in App.js like every other restricted
 * route, so the router's bounce is now the only caller for it). A hide
 * that missed any one of them
 * would send a coach to a page they can no longer see, which is worse than
 * the status quo: the app would bounce them somewhere they did not ask for
 * on a surface that used to work. One function, one answer, and grepping
 * for its name finds every place that has an opinion about "home".
 *
 * ADMIN STILL LANDS ON THE DASHBOARD. `AdminDashboard` (Dashboard.jsx) is
 * the admin's own view and nothing about #384 touches it — Martin's call was
 * specifically about the coach's.
 */

/** Home for a player, and the app's answer when the role is not yet known. */
export const PLAYER_HOME = "/dashboard";

/** Home for a coach since #384 item 3: My Players (`pages/CoachPortal.jsx`). */
export const COACH_HOME = "/coach-portal";

/**
 * The path this role should be sent to when nothing more specific applies.
 *
 * An unknown/absent role deliberately resolves to `PLAYER_HOME` rather than
 * throwing: several callers run while `AuthContext`'s `/auth/me` probe is
 * still in flight (`user` is `null`), and the dashboard is reachable by
 * every role — a coach who somehow arrives there is redirected on by the
 * router (App.js), so the worst case is one extra hop, never a dead end.
 */
export function homePathForRole(role) {
  return role === "coach" ? COACH_HOME : PLAYER_HOME;
}

/**
 * What to CALL that destination in a label a user reads (the app-shell logo
 * links, which say where they go). Kept next to the path so the two cannot
 * drift into a link that promises the dashboard and opens the portal.
 */
export function homeLabelForRole(role) {
  return role === "coach" ? "your players" : "your dashboard";
}
